import React from 'react';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import classNames from 'classnames';
import { isNullOrUndefinedOrEmpty } from 'rewire-common';
import { Observe } from 'rewire-core';
import { WithStyle, withStyles, FormView, Modal } from 'rewire-ui';
import { Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import FlowLogoFullDark from '../images/flow-logo-full-dark.svg';
import Routes from '../Routes';
import authService from '../services/AuthService';
import Dialog from '../ui/Dialog2';
import Confirmation from './Confirmation';
import setupAccountModel from './SetupAccountModel';
import { getInitials } from '../utility/StringUtils';

const styles = (theme: Theme) => ({
  root: {
    width: '100%',
    height: '100vh',
    backgroundColor: theme.palette.primary.main,
  },
  form: {
    flex: '1 0 auto',
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleWithLogoContainer: {
    '@media (max-width: 560px)': {
      flexDirection: 'column',
    },
  },
  titleText: {
    '@media (max-width: 560px)': {
      fontSize: '1rem',
    },
  },
  flowLogo: {
    margin: '5px 0px 5px 0px',
    width: '95px',
    '@media (max-width: 560px)': {
      width: '75px',
    },
  },
  upperContentContainer: {
    display: 'flex',
    flex: '1 0 auto',
    padding: '0px 16px',
    '@media (max-width: 560px)': {
      flexDirection: 'column-reverse',
    },
  },
  upperContentDetails: {
    flex: '1 0 auto',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 0px',
    flex: '1 0 auto',
  },
  lowerContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
  },
  lowerContentContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: '12px 16px',
    gap: '24px',
    '@media (max-width: 560px)': {
      flexDirection: 'column',
    },
  },
  avatarContainer: {
    flex: '1 0 auto',
    padding: '15px 0px 0px 16px',
    '@media (max-width: 560px)': {
      display: 'none',
    },
  },
  avatarMobileContainer: {
    flex: '1 0 auto',
    padding: '0px 0px 15px 0px',
    '@media (min-width: 561px)': {
      display: 'none',
    },
  },
  avatarRoot: {
    fontSize: '70px',
  },
  avatarMobileRoot: {
    fontSize: '56px',
  },
  avatarColorDefault: {
    backgroundColor: theme.palette.dialogPrimary.light,
  },
  dialogPaperRoot: {
    maxWidth: '750px',
  },
  footNoteContainer: {
    padding: '0px 16px',
  },
  footNote: {
    paddingTop: '8px',
    fontSize: '0.825em',
  },
  formErrorsContainer: {
    color: theme.palette.error.main,
  },
});

interface ISetupAccountProps {}

type SetupAccountProps = WithStyle<ReturnType<typeof styles>, ISetupAccountProps & RouteComponentProps<any>>;

class SetupAccount extends React.Component<SetupAccountProps> {
  constructor(props: SetupAccountProps) {
    super(props);

    const { token } = props.match.params;

    setupAccountModel.load(token);
  }

  componentWillUnmount() {
    setupAccountModel.reset();
  }

  renderTitle = (_dialog: Modal): JSX.Element => {
    return (
      <div className={this.props.classes.titleContainer}>
        <img alt="flow logo" src={FlowLogoFullDark} className={this.props.classes.flowLogo} />
        <Typography variant="h6" className={this.props.classes.titleText}>
          Please start by completing your profile
        </Typography>
      </div>
    );
  };

  renderDialogContents = React.memo((): JSX.Element => {
    const { classes } = this.props;

    return (
      <Observe
        render={() => (
          <FormView className={classes.form} form={setupAccountModel.form} onSubmit={setupAccountModel.actionFn('continue')}>
            <div className={classes.upperContentContainer}>
              <div className={classes.upperContentDetails}>
                {setupAccountModel.form.hasErrors && (
                  <div className={classes.formErrorsContainer}>
                    <Typography variant="body2">Form has errors. Please resolve in order to continue.</Typography>
                  </div>
                )}
                <div className={classes.content}>
                  <setupAccountModel.form.field.name.Editor className="span1" />
                </div>
                <div className={classes.content}>
                  <setupAccountModel.form.field.email.Editor className="span1" />
                </div>
                <div className={classes.content}>
                  <setupAccountModel.form.field.phone.Editor className="span1" />
                </div>

                <Observe
                  render={() =>
                    !setupAccountModel.isExistingOrganization && (
                      <div className={classes.content}>
                        <setupAccountModel.form.field.applicationTemplate.Editor />
                      </div>
                    )
                  }
                />
              </div>
              <div className={classes.avatarContainer}>
                <Observe
                  render={() => (
                    <setupAccountModel.form.field.avatar.Editor
                      avatarClasses={{ root: classes.avatarRoot, colorDefault: classes.avatarColorDefault }}
                      avatarChildren={
                        !isNullOrUndefinedOrEmpty(setupAccountModel.form.field.name.value)
                          ? getInitials(setupAccountModel.form.field.name.value)
                          : null
                      }
                    />
                  )}
                />
              </div>
              <div className={classes.avatarMobileContainer}>
                <Observe
                  render={() => (
                    <setupAccountModel.form.field.avatarMobile.Editor
                      avatarClasses={{ root: classes.avatarMobileRoot, colorDefault: classes.avatarColorDefault }}
                      avatarChildren={
                        !isNullOrUndefinedOrEmpty(setupAccountModel.form.field.name.value)
                          ? getInitials(setupAccountModel.form.field.name.value)
                          : null
                      }
                    />
                  )}
                />
              </div>
            </div>
            <Observe
              render={() =>
                !setupAccountModel.isSingleSignOn && (
                  <div className={classes.lowerContentContainer}>
                    <div className={classes.lowerContent}>
                      <setupAccountModel.form.field.password.Editor className="span1" />
                    </div>
                    <div className={classes.lowerContent}>
                      <setupAccountModel.form.field.confirmPassword.Editor className="span1" />
                    </div>
                  </div>
                )
              }
            />
            <Observe
              render={() =>
                !setupAccountModel.isExistingOrganization && (
                  <div className={classes.lowerContentContainer}>
                    <div className={classes.lowerContent}>
                      <setupAccountModel.form.field.organization.Editor className="span1" />
                    </div>
                    <div className={classes.lowerContent}>
                      <setupAccountModel.form.field.location.Editor className="span1" />
                    </div>
                  </div>
                )
              }
            />
            <div className={classNames(classes.content, classes.footNoteContainer)}>
              <Typography variant="body2" className={classes.footNote}>
                <em>* indicates a required field</em>
              </Typography>
            </div>
          </FormView>
        )}
      />
    );
  });

  render() {
    const { from } = (this.props.location.state as { from: string }) || { from: { pathname: Routes.Home } };
    const { classes } = this.props;

    return (
      <Observe
        render={() => {
          if (authService.isAuthenticated) {
            return <Redirect to={from} />;
          }

          return (
            <div className={classes.root}>
              {setupAccountModel.isComplete ? (
                <Confirmation
                  type="success"
                  redirectTo={Routes.Login}
                  message="Flow account created successfully. You will be redirected to the Sign In screen shortly."
                />
              ) : (
                <Dialog
                  dialog={setupAccountModel}
                  hideBackdrop={true}
                  disableTransition={true}
                  title={this.renderTitle}
                  classes={{ root: classes.dialogPaperRoot }}
                  maxWidth="md"
                >
                  <this.renderDialogContents />
                </Dialog>
              )}
            </div>
          );
        }}
      />
    );
  }
}

export default withStyles(styles, SetupAccount);
